// composables/useFormAssociated.ts
import { onMounted, onBeforeUnmount, ref, watch, type Ref, toValue } from 'vue';

/**
 * Value accepted by ElementInternals.setFormValue.
 * - string: posted as-is
 * - File: posted as a file
 * - FormData: posted as multiple entries (useful for multi-select)
 * - null: omit the field from submission
 */
export type FormValue = string | File | FormData | null;

/**
 * Options for useFormAssociated.
 * @template T The component's model type
 */
export interface UseFormAssociatedOptions<T = unknown> {
  /**
   - Ref to a native <input> inside your component.
   - Its validity is used to mirror constraint validation to the host.
   - It must be part of the component's shadow DOM (so we can find the host).
   */
  inputRef: Ref<HTMLInputElement | null>;
  /**
   - The component's current model value.
   - Changes to this ref update the element’s form value.
   */
  modelValue: Ref<T | null | undefined>;
  /** Disabled state (ref or boolean). When disabled, the element is omitted from form submission. */
  disabled?: Ref<boolean | undefined> | boolean;
  /** Required state (ref or boolean). Affects validity mirroring from the native input. */
  required?: Ref<boolean | undefined> | boolean;
  /**
   * Map your model to a form value.
   * Return null to omit the field from submission.
   * Defaults to: undefined|null|'' => null, otherwise String(v).
   */
  toFormValue?: (v: T | null | undefined) => FormValue;
}

/**
 * useFormAssociated
 *
 * Binds a Vue custom element to native HTML forms via ElementInternals.
 * - Attaches ElementInternals to the host custom element.
 * - Mirrors the native input's validity to the host.
 * - Sets the form value from your model (with optional mapping).
 * - Handles <form> reset events.
 *
 * Requirements:
 * - Register your component with formAssociated: true when calling defineEConnectElement/defineCustomElement.
 * - Place a name attribute on the host custom element (<e-xyz name="...">) to include it in submissions.
 * - Provide a ref to a native input living in the component's shadow DOM.
 *
 * Example:
 * const inputRef = ref<HTMLInputElement|null>(null);
 * const [value] = defineModel<string>();
 * const required = toRef(props, 'required');
 * const { validate } = useFormAssociated({
 *   inputRef,
 *   modelValue: value,
 *   required,
 *   toFormValue: v => v?.trim() ? v : null
 * });
 */
export function useFormAssociated<T = unknown>({
  inputRef,
  modelValue,
  disabled,
  required,
  toFormValue
}: UseFormAssociatedOptions<T>) {
  const internals = ref<ElementInternals | null>(null);

  const mapToFormValue =
    toFormValue ??
    ((v: T | null | undefined): FormValue => {
      if (v === undefined || v === null || v === '') return null;
      return String(v);
    });

  function getHostEl(): HTMLElement | null {
    const root = inputRef.value?.getRootNode() as ShadowRoot | undefined;
    return (root?.host as HTMLElement) ?? null;
  }

  function syncFormValue() {
    const ints = internals.value;
    if (!ints) return;
    const isDisabled = typeof disabled === 'object' ? !!toValue(disabled) : !!disabled;
    const fv = isDisabled ? null : mapToFormValue(modelValue.value as T);
    ints.setFormValue(fv);
  }

  function validate() {
    const input = inputRef.value;
    const ints = internals.value;
    if (!input || !ints) return;

    if (input.checkValidity()) {
      ints.setValidity({});
    } else {
      ints.setValidity(input.validity, input.validationMessage, input);
    }
  }

  function handleFormReset() {
    // run after UA resets controls so the input has its new value
    queueMicrotask(() => {
      syncFormValue();
      validate();
    });
  }

  onMounted(() => {
    const host = getHostEl();
    if (!host || typeof (host as any).attachInternals !== 'function') return;

    try {
      internals.value = (host as any).attachInternals();

      // Initial sync
      syncFormValue();
      validate();

      // Reflect disabled state in internals.states (helps a11y tooling)
      const isDisabled = typeof disabled === 'object' ? !!toValue(disabled) : !!disabled;
      if (isDisabled) internals.value?.states.add('disabled');

      internals.value?.form?.addEventListener('reset', handleFormReset);
    } catch {
      // ElementInternals not supported; safely no-op
    }
  });

  onBeforeUnmount(() => {
    internals.value?.form?.removeEventListener('reset', handleFormReset);
  });

  // Keep internals in sync with model changes
  watch(modelValue, () => {
    syncFormValue();
    validate();
  });

  // Track disabled changes (ref form)
  if (typeof disabled === 'object') {
    watch(disabled, d => {
      const ints = internals.value;
      if (!ints) return;
      if (d) ints.states.add('disabled');
      else ints.states.delete('disabled');
      syncFormValue();
    });
  }

  // Track required changes (ref form) to refresh validity
  if (typeof required === 'object') {
    watch(required, () => {
      validate();
    });
  }

  return {
    /** The ElementInternals instance once attached (or null if unsupported). */
    internals,
    /** Manually re-run validity syncing based on the current native input. */
    validate,
    /** Manually push the current model through to ElementInternals as a form value. */
    sync: syncFormValue
  };
}
